import { computed, ref, type ComputedRef, type Ref } from "vue";
import type { AuthenticationResult } from "@azure/msal-browser";
import type { AuthProvider } from "@ics/auth-api";
import {
  getActiveProvider,
  getProviders,
  initProviderRegistry,
  logoutActiveProvider,
  setActiveProvider
} from "./providerRegistry";
import { isInteractiveAuthAllowed } from "./bootPhase";
import { resetSessionStores, syncSessionStores } from "./sessionSync";

/**
 * Session-sjekk for router-guarden. Forutsetter at `warmRestoreSession()` har
 * kjørt i `main.js` mellom `initProviderRegistry()` og `app.use(router)` — etter
 * det er `provider.currentAccount()` autoritativ uten ad-hoc fallback. Returnerer
 * `false` hvis warm-restore feilet eller bruker ikke har aktiv sesjon; router-guard
 * tar det videre til `saveIntendedRoute()` + redirect til Login.
 */
export function hasActiveSession (): boolean {
  const provider = getActiveProvider();
  return provider !== null && provider.currentAccount() !== null;
}

/**
 * Henter token via aktiv provider. Brukes av SignalR-aksesstoken-fabrikker og
 * andre konsumenter som trenger silent-only oppførsel (ingen popup ved feil).
 * Ingen aktiv provider (registryen kom aldri opp — config nede) → `null`;
 * silent-stien kaster aldri og har ingen parallell legacy-motor (task 2677).
 */
export async function getShellAccessTokenSilent (): Promise<AuthenticationResult | null> {
  const provider = getActiveProvider();
  if (provider) {
    return await provider.getTokenSilent();
  }
  return null;
}

/**
 * Henter token via aktiv provider; trigger popup hvis silent feiler. Brukes av
 * API-klienter (axios, module-host) der popup-fallback er akseptabel. Ingen
 * aktiv provider → `null` (kalleren får 401 → sentral redirect-håndtering).
 */
export async function getShellAccessTokenSilentOrPopup (): Promise<AuthenticationResult | null> {
  // Boot-vern (WI 2118): før appen er montert finnes ingen user gesture. Et
  // popup-fallback her ville bli taust blokkert av nettleseren, og MSAL ville
  // vente ut sin popup-bridge-timeout (B2C: opptil 10 min) → `app.mount('#app')`
  // kalles aldri → evig «Starter ICS…»-spinner. Kjør derfor silent-only til
  // `markAppInteractive()` er kalt; feiler silent under boot, returneres null og
  // router-guarden ruser til Login (ingen hang).
  if (!isInteractiveAuthAllowed()) {
    return await getShellAccessTokenSilent();
  }
  const provider = getActiveProvider();
  if (provider) {
    return await provider.getTokenSilentOrPopup();
  }
  return null;
}

/**
 * Login-redirect via aktiv provider; brukes av popup-blokkert-fallback og den
 * sentrale 401-håndteringen. Uten aktiv provider finnes ingen motor å
 * redirecte med (registryen kom aldri opp) → no-op med warn (task 2677).
 */
export async function shellLoginRedirect (intendedRoute?: string): Promise<void> {
  const provider = getActiveProvider();
  if (!provider) {
    console.warn("[AuthShell] shellLoginRedirect uten aktiv provider — ingen redirect mulig");
    return;
  }
  await provider.loginRedirect(intendedRoute);
}

/**
 * Reaktiv state for aktiv provider, eksponert til Vue-komponenter (login-UX).
 * Oppdateres ved `setActiveProviderId()`/`logout()`.
 */
const activeProviderId = ref<string | null>(null);

function syncActiveFromRegistry (): void {
  activeProviderId.value = getActiveProvider()?.id ?? null;
}

/**
 * Vue-composable for å bruke auth-shell fra komponenter (eks. provider-selector
 * i login-UX). Wrapper providerRegistry med reaktiv state.
 *
 * Bruksmønster:
 * ```ts
 * const { providers, activeProvider, setProvider, login, logout } = useAuthShell();
 * await initialize();
 * ```
 *
 * For ikke-Vue-kontekst (eks. axiosClient): bruk providerRegistry-funksjonene direkte.
 */
export function useAuthShell (): {
  providers: ComputedRef<readonly AuthProvider[]>;
  activeProvider: ComputedRef<AuthProvider | null>;
  activeProviderId: Ref<string | null>;
  initialize: () => Promise<void>;
  setProvider: (id: string) => void;
  login: () => Promise<boolean>;
  logout: () => Promise<void>;
} {
  const providers = computed(() => getProviders());
  const activeProvider = computed(() => getActiveProvider());

  const initialize = async (): Promise<void> => {
    await initProviderRegistry();
    syncActiveFromRegistry();
  };

  const setProvider = (id: string): void => {
    setActiveProvider(id);
    syncActiveFromRegistry();
  };

  const login = async (): Promise<boolean> => {
    const provider = getActiveProvider();
    if (!provider) {
      throw new Error("Ingen aktiv provider — kall initialize() først");
    }
    const ok = await provider.login();
    if (ok) {
      // Synkroniser shared Pinia-stores + globale refs som template/komponenter
      // leser. Delt med redirect-stien (providerRegistry) via syncSessionStores
      // (WI 2196) — EntraProvider gjør dette via helpers/auth.login() (gammel flyt),
      // men B2CProvider har egen MSAL-instans og må gjøre det sentralt her.
      syncSessionStores(provider.currentAccount());
    }
    return ok;
  };

  const logout = async (): Promise<void> => {
    await logoutActiveProvider();
    // WI 2255: tøm session-scopet tilstand så neste innlogging i samme fane ikke
    // arver forrige brukers tilgang (logout reloader ikke fanen).
    resetSessionStores();
    syncActiveFromRegistry();
  };

  return { providers, activeProvider, activeProviderId, initialize, setProvider, login, logout };
}
